/*
  Find Your Heat — interactive dial
  ------------------------------------------------------------------
  Progressive enhancement only. The server renders every band as a stacked,
  readable guide plus a working form with a native <select> for the hair
  profile. This script replaces that with the dial and adds .hd--enhanced,
  which is the ONLY thing that collapses the stack. If it never runs, the
  section is still complete, readable and submittable.

  Ways to drive the dial:
    1. pointer drag / tap anywhere on the dial face or its printed numbers
    2. arrow keys, Home and End (the dial is a real role="slider")
    3. the native <select>, when JS has not enhanced

  Units: bands are authored in Celsius; Fahrenheit is derived. The °F/°C
  toggle under the dial repaints the numbers without moving the dial, and
  never changes the Celsius value written to the customer tag.

  Affordance, so nobody has to read an instruction:
    - while the dial is in view, a hand grabs the knob and turns it one band
      and back on a loop (a still hand for reduced motion; ended by any touch)
    - a short palm-icon hint sits under the dial until first use
    - while dragging, .hd--dragging lets the surrounding copy step back

  Geometry: a 270-degree arc starting at 135 degrees (bottom-left), sweeping
  clockwise through left, top and right to 405 degrees (bottom-right). Angles
  are screen-space, measured clockwise from the positive x-axis because SVG's
  y grows downward.
*/
(function () {
  'use strict';

  var CX = 100;
  var CY = 100;
  var R = 78;
  var LABEL_R = 97;
  var START_ANGLE = 135;
  var SWEEP = 270;
  var END_ANGLE = START_ANGLE + SWEEP;

  var COUNT_MS = 380;

  /* Hand-cue timeline, ms from the start of one loop. The knob's own
     transition (heat-dial.css, .hd__knob-group) is 480ms, so each turn has
     room to finish before the next step. */
  var DEMO = {
    grab: 600,
    turn: 1000,
    back: 2300,
    release: 3400,
    cycle: 5200
  };

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function toRad(deg) {
    return (deg * Math.PI) / 180;
  }

  function pointOnArc(angle, radius) {
    return {
      x: CX + radius * Math.cos(toRad(angle)),
      y: CY + radius * Math.sin(toRad(angle))
    };
  }

  function svgEl(name) {
    return document.createElementNS('http://www.w3.org/2000/svg', name);
  }

  function clamp(value, min, max) {
    return value < min ? min : value > max ? max : value;
  }

  /*
    ---------- Units (2026-09-21, client revision PDF item 3) ----------
    Bands are authored in Celsius and that number is what the customer tag
    records, so a visitor flipping the toggle never changes what the store
    stores. Fahrenheit is derived, here and in Liquid, from the same number.

    The choice is remembered in sessionStorage, not localStorage: it is a
    reading preference for this visit, not a profile. Every access is
    wrapped — Safari's private mode throws on write, and a thrown error here
    would take the whole dial down with it.
  */
  var UNIT_KEY = 'veaudry:heat-unit';

  function toFahrenheit(celsius) {
    return Math.round((celsius * 9) / 5 + 32);
  }

  function readStoredUnit() {
    try {
      var stored = window.sessionStorage.getItem(UNIT_KEY);
      return stored === 'c' || stored === 'f' ? stored : null;
    } catch (e) {
      return null;
    }
  }

  function storeUnit(unit) {
    try {
      window.sessionStorage.setItem(UNIT_KEY, unit);
    } catch (e) {
      /* preference only — never worth failing the interaction over */
    }
  }

  function HeatDial(root) {
    this.root = root;
    this.dial = root.querySelector('[data-hd-dial]');
    this.panels = Array.prototype.slice.call(root.querySelectorAll('[data-hd-panel]'));

    // A dial with fewer than two positions is not a dial. Leave the stacked
    // fallback in place rather than shipping an inert control.
    if (!this.dial || this.panels.length < 2) return;

    this.progress = root.querySelector('[data-hd-progress]');
    this.knob = root.querySelector('[data-hd-knob]');
    this.hand = root.querySelector('[data-hd-hand]');
    this.handOrbit = root.querySelector('[data-hd-hand-orbit]');
    this.handUpright = root.querySelector('[data-hd-hand-upright]');
    this.ticksGroup = root.querySelector('[data-hd-ticks]');
    this.labelsGroup = root.querySelector('[data-hd-labels]');
    this.readout = root.querySelector('[data-hd-readout]');
    /*
      data-hd-readout-TEMP, deliberately not data-hd-temp: every panel carries
      data-hd-temp as band data, so querying data-hd-temp here would return a
      PANEL and animateTemp would wipe it down to a bare number. Do not
      "simplify" these two attribute names back together.
    */
    this.tempEl = root.querySelector('[data-hd-readout-temp]');
    this.unitEl = root.querySelector('[data-hd-readout-unit]');
    this.unitButtons = Array.prototype.slice.call(root.querySelectorAll('[data-hd-unit-btn]'));
    this.labelEl = root.querySelector('[data-hd-band-label]');
    this.ctaBand = root.querySelector('[data-hd-cta-band]');
    this.tagsInput = root.querySelector('[data-hd-tags]');
    this.select = root.querySelector('[data-hd-select]');
    this.live = root.querySelector('[data-hd-live]');

    this.baseTags = this.tagsInput ? this.tagsInput.value : '';

    this.bands = this.panels.map(function (panel) {
      var celsius = parseInt(panel.getAttribute('data-hd-temp'), 10) || 0;
      var fahrenheit = parseInt(panel.getAttribute('data-hd-temp-f'), 10);
      return {
        el: panel,
        temp: celsius,
        tempF: isNaN(fahrenheit) ? toFahrenheit(celsius) : fahrenheit,
        label: panel.getAttribute('data-hd-label') || '',
        tag: panel.getAttribute('data-hd-tag') || ''
      };
    });

    // The visitor's own choice wins over the merchant's default, so the unit
    // does not reset when they scroll past this section on another page.
    this.unit = readStoredUnit() || (root.getAttribute('data-hd-unit') === 'c' ? 'c' : 'f');

    this.count = this.bands.length;
    this.index = clamp(parseInt(root.getAttribute('data-hd-start'), 10) || 0, 0, this.count - 1);
    this.displayedTemp = this.tempOf(this.bands[this.index]);
    this.touched = false;
    this.demoTimers = [];

    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);

    this.buildTicks();
    this.measureProgress();
    this.bind();

    root.classList.add('hd--enhanced');
    this.apply(this.index, { force: true, silent: true });
    this.scheduleDemo();
  }

  /* ---------- units ---------- */

  HeatDial.prototype.tempOf = function (band) {
    return this.unit === 'c' ? band.temp : band.tempF;
  };

  HeatDial.prototype.unitSuffix = function () {
    return this.unit === 'c' ? '°C' : '°F';
  };

  /*
    Switching units repaints every number at once — the readout, the printed
    tick labels, the slider's accessible value — without moving the dial.
    The band the visitor chose is not a unit, so it does not change here.
  */
  HeatDial.prototype.setUnit = function (unit) {
    if (unit !== 'c' && unit !== 'f') return;
    if (unit === this.unit) return;

    this.unit = unit;
    storeUnit(unit);

    this.unitButtons.forEach(function (button) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-hd-unit-btn') === unit));
    });

    if (this.unitEl) this.unitEl.textContent = this.unitSuffix();

    var self = this;
    this.labels.forEach(function (label, i) {
      label.textContent = String(self.tempOf(self.bands[i]));
    });

    // force + silent: the same band, restated. Counting the number across a
    // unit change would read as the dial having moved, and announcing it
    // would talk over a visitor who only pressed a formatting control.
    this.apply(this.index, { force: true, silent: true });
  };

  HeatDial.prototype.buildTicks = function () {
    this.ticks = [];
    this.labels = [];
    for (var i = 0; i < this.count; i++) {
      var angle = START_ANGLE + (SWEEP * i) / (this.count - 1);

      if (this.ticksGroup) {
        var outer = pointOnArc(angle, R - 10);
        var inner = pointOnArc(angle, R - 4);
        var line = svgEl('line');
        line.setAttribute('x1', outer.x.toFixed(2));
        line.setAttribute('y1', outer.y.toFixed(2));
        line.setAttribute('x2', inner.x.toFixed(2));
        line.setAttribute('y2', inner.y.toFixed(2));
        line.setAttribute('class', 'hd__tick');
        this.ticksGroup.appendChild(line);
        this.ticks.push(line);
      }

      if (this.labelsGroup) {
        var at = pointOnArc(angle, LABEL_R);
        var text = svgEl('text');
        text.setAttribute('x', at.x.toFixed(2));
        text.setAttribute('y', at.y.toFixed(2));
        text.setAttribute('class', 'hd__tick-label');
        text.textContent = String(this.tempOf(this.bands[i]));
        this.labelsGroup.appendChild(text);
        this.labels.push(text);
      }
    }
  };

  HeatDial.prototype.measureProgress = function () {
    if (!this.progress) return;
    // getTotalLength is exact for the arc path and avoids re-deriving the
    // circumference by hand if the radius is ever tuned in the markup.
    this.arcLength = this.progress.getTotalLength();
    this.progress.style.strokeDasharray = this.arcLength;
    this.progress.style.strokeDashoffset = this.arcLength;
  };

  HeatDial.prototype.bind = function () {
    this.dial.addEventListener('pointerdown', this.onPointerDown);
    this.dial.addEventListener('keydown', this.onKeyDown);

    var self = this;

    // Focusing anything in the section (the dial by Tab, or the email field)
    // ends the hand cue, so the CTA and hidden tags stop changing under
    // someone who is about to submit.
    this.root.addEventListener('focusin', function () {
      self.markTouched();
    });

    if (this.select) {
      this.select.addEventListener('change', function () {
        var next = parseInt(self.select.value, 10);
        if (!isNaN(next)) self.apply(next);
      });
    }

    /*
      The unit toggle. Server-rendered pressed states reflect the merchant
      default; a stored preference can disagree with them, so sync once here
      before anyone can click.
    */
    this.unitButtons.forEach(function (button) {
      button.setAttribute(
        'aria-pressed',
        String(button.getAttribute('data-hd-unit-btn') === self.unit)
      );
      button.addEventListener('click', function () {
        self.setUnit(button.getAttribute('data-hd-unit-btn'));
      });
    });

    if (this.unitEl) this.unitEl.textContent = this.unitSuffix();

    this.bindDisclosures();
  };

  /*
    ---------- "Why this heat" ----------
    The markup is a real <details>, which already handles click, Enter/Space
    and screen readers, and works with the script absent. All this adds is
    opening on hover and on keyboard focus (client revision PDF item 3), by
    driving the same `open` property — so there is one source of truth for
    whether the explanation is showing, and no second state to fall out of
    sync with the marker-less summary.

    Hover only where hovering is real: on a touch screen `pointerenter`
    fires on tap, which would fight the click <details> already handles.
  */
  HeatDial.prototype.bindDisclosures = function () {
    var canHover = !window.matchMedia || window.matchMedia('(hover: hover)').matches;

    Array.prototype.forEach.call(this.root.querySelectorAll('[data-hd-more]'), function (more) {
      /*
        A tap is two events: focusin, then click. focusin opened the panel and
        the click's native <details> toggle shut it again in the same gesture,
        so on a phone the first tap appeared to do nothing and only the second
        one opened it. (Measured 2026-10-05, English and Arabic.)

        The click is now handled here instead of by <details>: if the panel was
        opened a moment ago by this same gesture, leave it open; otherwise
        toggle. 500ms is well above the focus→click gap of a tap and well
        below a deliberate second press.
      */
      var openedAt = 0;
      var SAME_GESTURE_MS = 500;
      function openFromGesture() {
        if (!more.open) openedAt = Date.now();
        more.open = true;
      }

      var summary = more.querySelector('summary');
      if (summary) {
        summary.addEventListener('click', function (event) {
          event.preventDefault();
          if (more.open && Date.now() - openedAt < SAME_GESTURE_MS) return;
          more.open = !more.open;
        });
      }

      if (canHover) {
        more.addEventListener('mouseenter', openFromGesture);
        more.addEventListener('mouseleave', function () {
          more.open = false;
        });
      }

      more.addEventListener('focusin', openFromGesture);
      more.addEventListener('focusout', function (event) {
        // Keep it open while focus moves to the link inside the panel.
        if (!more.contains(event.relatedTarget)) more.open = false;
      });
    });
  };

  /* ---------- first-use affordance: the hand cue ---------- */

  /*
    While the dial is in view and nobody has used it, a hand repeats one short
    gesture: fade in beside the knob, close into a grab, turn the dial one
    band and back, let go, rest. It pauses whenever the dial leaves the
    viewport, and ends for good on any pointer, key or focus inside the
    section. Reduced motion (or no IntersectionObserver) gets the hand parked
    on the knob as a still image instead.
  */
  HeatDial.prototype.scheduleDemo = function () {
    if (!this.hand) return;

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      this.root.classList.add('hd--hand-static');
      return;
    }

    var self = this;
    this.demoObserver = new IntersectionObserver(
      function (entries) {
        if (self.touched) return;
        if (entries[0].isIntersecting) {
          self.startDemo();
        } else {
          self.stopDemo();
        }
      },
      { threshold: 0.5 }
    );
    this.demoObserver.observe(this.dial);
  };

  HeatDial.prototype.startDemo = function () {
    if (this.demoRunning || this.touched) return;
    this.demoRunning = true;
    this.demoHome = this.index;
    this.runDemoCycle();
  };

  HeatDial.prototype.runDemoCycle = function () {
    var self = this;
    var home = this.demoHome;
    var away = home < this.count - 1 ? home + 1 : home - 1;
    var cls = this.root.classList;
    var at = function (ms, fn) {
      self.demoTimers.push(window.setTimeout(fn, ms));
    };

    cls.add('hd--hand-in');
    at(DEMO.grab, function () {
      cls.add('hd--hand-grab');
    });
    at(DEMO.turn, function () {
      self.apply(away, { silent: true });
    });
    at(DEMO.back, function () {
      self.apply(home, { silent: true });
    });
    at(DEMO.release, function () {
      cls.remove('hd--hand-grab', 'hd--hand-in');
    });
    at(DEMO.cycle, function () {
      self.demoTimers = [];
      self.runDemoCycle();
    });
  };

  HeatDial.prototype.stopDemo = function () {
    this.demoTimers.forEach(window.clearTimeout);
    this.demoTimers = [];
    this.root.classList.remove('hd--hand-in', 'hd--hand-grab');
    if (this.demoRunning) {
      this.demoRunning = false;
      // Never leave the dial parked on the demo's "away" band.
      if (this.index !== this.demoHome) this.apply(this.demoHome, { silent: true });
    }
  };

  HeatDial.prototype.markTouched = function () {
    if (this.touched) return;
    this.touched = true;
    this.stopDemo();
    if (this.demoObserver) this.demoObserver.disconnect();
    this.root.classList.add('hd--touched');
  };

  /* ---------- pointer ---------- */

  HeatDial.prototype.indexFromEvent = function (event) {
    var rect = this.dial.getBoundingClientRect();
    var cx = rect.left + rect.width / 2;
    var cy = rect.top + rect.height / 2;
    var angle = (Math.atan2(event.clientY - cy, event.clientX - cx) * 180) / Math.PI;
    if (angle < 0) angle += 360;

    /*
      The arc has a dead wedge at the bottom, between 45 and 135 degrees.
      A pointer landing in it must resolve to whichever end is nearer,
      otherwise a tap just below the dial would silently jump to maximum.
      Straight down (90) is the dividing line.
    */
    if (angle < START_ANGLE) {
      if (angle <= 45) {
        angle += 360;
      } else if (angle < 90) {
        angle = END_ANGLE;
      } else {
        angle = START_ANGLE;
      }
    }
    angle = clamp(angle, START_ANGLE, END_ANGLE);

    var t = (angle - START_ANGLE) / SWEEP;
    return clamp(Math.round(t * (this.count - 1)), 0, this.count - 1);
  };

  HeatDial.prototype.onPointerDown = function (event) {
    this.markTouched();
    this.dragging = true;
    this.root.classList.add('hd--dragging', 'hd--pointer');
    if (this.dial.setPointerCapture) {
      try {
        this.dial.setPointerCapture(event.pointerId);
      } catch (e) {
        /* capture is an optimisation, not a requirement */
      }
    }
    this.dial.focus({ preventScroll: true });
    this.apply(this.indexFromEvent(event));
    this.dial.addEventListener('pointermove', this.onPointerMove);
    this.dial.addEventListener('pointerup', this.onPointerUp);
    this.dial.addEventListener('pointercancel', this.onPointerUp);
    event.preventDefault();
  };

  HeatDial.prototype.onPointerMove = function (event) {
    if (!this.dragging) return;
    this.apply(this.indexFromEvent(event));
    event.preventDefault();
  };

  HeatDial.prototype.onPointerUp = function () {
    this.dragging = false;
    this.root.classList.remove('hd--dragging');
    this.dial.removeEventListener('pointermove', this.onPointerMove);
    this.dial.removeEventListener('pointerup', this.onPointerUp);
    this.dial.removeEventListener('pointercancel', this.onPointerUp);
  };

  /* ---------- keyboard ---------- */

  HeatDial.prototype.onKeyDown = function (event) {
    var next = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next = this.index + 1;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        next = this.index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.count - 1;
        break;
      case 'PageUp':
        next = this.index + 2;
        break;
      case 'PageDown':
        next = this.index - 2;
        break;
      default:
        return;
    }
    event.preventDefault();
    this.markTouched();
    this.root.classList.remove('hd--pointer');
    this.apply(clamp(next, 0, this.count - 1));
  };

  /* ---------- state ---------- */

  /*
    opts.force  — re-apply even if the index is unchanged (initial paint)
    opts.silent — do not announce to screen readers (initial paint and the
                  scroll-in demo, which are not the visitor's own actions)
  */
  HeatDial.prototype.apply = function (index, opts) {
    opts = opts || {};
    if (!opts.force && index === this.index) return;

    var previousTemp = this.tempOf(this.bands[this.index]);
    this.index = index;
    var band = this.bands[index];
    var t = this.count > 1 ? index / (this.count - 1) : 0;

    this.root.style.setProperty('--heat', t.toFixed(3));

    if (this.progress && this.arcLength) {
      this.progress.style.strokeDashoffset = this.arcLength * (1 - t);
    }

    if (this.knob) {
      var angle = START_ANGLE + SWEEP * t;
      this.knob.setAttribute('transform', 'rotate(' + angle.toFixed(2) + ' ' + CX + ' ' + CY + ')');
      if (this.handOrbit && this.handUpright) {
        this.handOrbit.setAttribute('transform', 'rotate(' + angle.toFixed(2) + ' ' + CX + ' ' + CY + ')');
        // 178,100 is the knob's un-rotated centre in the markup.
        this.handUpright.setAttribute('transform', 'rotate(' + (-angle).toFixed(2) + ' 178 100)');
      }
    }

    for (var i = 0; i < this.count; i++) {
      if (this.ticks[i]) this.ticks[i].classList.toggle('is-lit', i <= index);
      if (this.labels[i]) this.labels[i].classList.toggle('is-active', i === index);
    }

    this.panels.forEach(function (panel, j) {
      var active = j === index;
      panel.classList.toggle('is-active', active);
      /* Panels share one grid cell sized to the tallest, so an explanation
         left open on a hidden panel would keep its height reserved under
         every other setting. Collapse it on the way out. */
      if (!active) {
        var more = panel.querySelector('.hd__more[open]');
        if (more) more.open = false;
      }
    });

    if (this.labelEl) this.labelEl.textContent = band.label;
    if (this.ctaBand) this.ctaBand.textContent = band.label;
    if (this.select) this.select.value = String(index);

    if (this.tagsInput) {
      var parts = [this.baseTags];
      if (band.tag) parts.push(band.tag);
      /* Always Celsius, whatever the visitor is reading. The tag is a
         segmentation key on the customer record, so it has to mean the same
         thing for every signup — two spellings of one band would split the
         audience in the email tool. */
      parts.push('heat:' + band.temp);
      this.tagsInput.value = parts.join(', ');
    }

    var shown = this.tempOf(band);
    var suffix = this.unitSuffix();

    this.dial.setAttribute('aria-valuenow', String(index));
    this.dial.setAttribute('aria-valuetext', shown + suffix + ', ' + band.label);
    if (this.live && !opts.silent) this.live.textContent = shown + suffix + ' — ' + band.label;

    this.animateTemp(opts.force ? shown : previousTemp, shown);
    if (!opts.force) this.pulse();
  };

  /* Counts the readout up or down rather than snapping, so the number reads
     as a dial being turned instead of a value being replaced. */
  HeatDial.prototype.animateTemp = function (from, to) {
    if (!this.tempEl) return;

    if (this.countFrame) {
      window.cancelAnimationFrame(this.countFrame);
      this.countFrame = null;
    }
    if (this.countFallback) {
      window.clearTimeout(this.countFallback);
      this.countFallback = null;
    }

    if (from === to || prefersReducedMotion()) {
      this.tempEl.textContent = String(to);
      this.displayedTemp = to;
      return;
    }

    var self = this;
    var startTime = null;

    var step = function (now) {
      if (startTime === null) startTime = now;
      var progress = Math.min((now - startTime) / COUNT_MS, 1);
      var eased = 1 - Math.pow(1 - progress, 3);
      var value = Math.round(from + (to - from) * eased);
      if (value !== self.displayedTemp) {
        self.displayedTemp = value;
        self.tempEl.textContent = String(value);
      }
      if (progress < 1) {
        self.countFrame = window.requestAnimationFrame(step);
      } else {
        self.countFrame = null;
        self.tempEl.textContent = String(to);
        self.displayedTemp = to;
      }
    };

    this.countFrame = window.requestAnimationFrame(step);

    /*
      requestAnimationFrame does not fire in a document that is not rendering
      (a background tab, a hidden container), which would strand the number
      on the previous band. This settles it regardless.
    */
    this.countFallback = window.setTimeout(function () {
      self.countFallback = null;
      if (self.countFrame) {
        window.cancelAnimationFrame(self.countFrame);
        self.countFrame = null;
      }
      self.tempEl.textContent = String(to);
      self.displayedTemp = to;
    }, COUNT_MS + 200);
  };

  HeatDial.prototype.pulse = function () {
    if (!this.readout || prefersReducedMotion()) return;
    var el = this.readout;
    el.classList.remove('is-stepping');
    // Reading offsetWidth forces a reflow so removing and re-adding the class
    // restarts the animation instead of being collapsed into a no-op.
    void el.offsetWidth;
    el.classList.add('is-stepping');
  };

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-hd]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.hdInitialised) return;
      root.hdInitialised = true;
      new HeatDial(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor re-renders the section's HTML on every settings change.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
